import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import SignIn from "./SignIn";
import SignUp from "./SignUp";
import OverviewPage from "./pages/OverviewPage";
import PressureVelocityPage from "./pages/PressureVelocityPage";
import ShutInPage from "./pages/ShutInPage";
import AfterFlowPage from "./pages/AfterFlowPage";
import ModelPage from "./pages/ModelPage";
import TensorBoardPage from "./pages/TensorBoardPage";
import InsightsPage from "./pages/InsightsPage";
import RootCausePage from "./pages/RootCausePage";
import AnomalyPage from "./pages/AnomalyPage";
import AIControllerPage from "./pages/AIControllerPage";
import GLRFeasibilityPage from "./pages/GLRFeasibilityPage";
import ComparativeAnalysisPage from "./pages/ComparativeAnalysisPage";
import EPACompliancePage from "./pages/EPACompliancePage";
import MethodComparisonPage from "./pages/MethodComparisonPage";
import GAPLAnalysisPage from "./pages/GAPLAnalysisPage";
import PAGLAnalysisPage from "./pages/PAGLAnalysisPage";
import GasLiftPerformancePage from "./pages/GasLiftPerformancePage";
import MandrelValveManagerPage from "./pages/MandrelValveManagerPage";
import InjectionDashboardPage from "./pages/InjectionDashboardPage";
import { useAIAdvice } from "./useAIAdvice";

import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ScatterChart, Scatter, ZAxis,
  ReferenceLine, ComposedChart, PieChart, Pie, Cell
} from "recharts";

import { WellProvider, useWell } from "./WellContext";
import { ThemeProvider, useTheme, getColors } from "./ThemeContext";
import { API } from "./config/api";
/* DESIGN TOKENS*/
const C = {
  bg:"#050b14", panel:"#080f1e", card:"#0b1525", border:"#0f2040", border2:"#162a4a",
  cyan:"#00d4ff", teal:"#00ffcc", amber:"#ffb84d", pink:"#ff4da6", purple:"#a855f7",
  blue:"#3b9eff", red:"#ff3d5a", green:"#00ff88", orange:"#ff7c2a", lime:"#b0ff4a",
  text:"#e2eaf8", muted:"#4a6080", muted2:"#2a3f60",
};

function applyTheme(colors) { Object.assign(C, colors); }
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

/* GLOBAL CSS*/
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@600;700;800&family=Barlow+Condensed:wght@500;600;700;800&family=Barlow:wght@300;400;500;600;700&family=Roboto+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #050b14; color: #e2eaf8; transition: background 0.3s, color 0.3s; }
  @keyframes pulse-ring { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(2.8); opacity: 0; } }
  @keyframes ticker-scroll { 0% { transform: translateX(100vw); } 100% { transform: translateX(-200%); } }
  .ticker-bar:hover .ticker-text { animation-play-state: paused !important; }
  @keyframes fade-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes scan-line { 0% { top: 0%; } 100% { top: 100%; } }
  @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
  @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes counter-spin { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
  @keyframes glow-pulse { 0%, 100% { box-shadow: 0 0 8px #00d4ff44; } 50% { box-shadow: 0 0 24px #00d4ff88, 0 0 48px #00d4ff22; } }
  @keyframes bar-fill { from { width: 0%; } to { width: var(--target-width); } }
  .card-hover { transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease; }
  .card-hover:hover { transform: translateY(-4px); border-color: #00d4ff55 !important; box-shadow: 0 12px 40px #00d4ff18; }
  .tab-btn { transition: all 0.18s ease; cursor: pointer; border: none; font-family: 'Syne', sans-serif; font-weight: 700; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; padding: 7px 14px; border-radius: 6px; }
  .tab-btn:hover { filter: brightness(1.2); }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: #080f1e; }
  ::-webkit-scrollbar-thumb { background: #162a4a; border-radius: 3px; }
`;

function injectCSS(css) {
  if(document.getElementById("plunger-css")) return;
  const s=document.createElement("style"); s.id="plunger-css"; s.textContent=css;
  document.head.appendChild(s);
}

/* ANIMATED NUMBER */
function AnimNum({ to, dec=1, duration=1200, prefix="", suffix="" }) {
  const [val, setVal] = useState(null);
  const ref = useRef();
  useEffect(() => {
    if(to==null||isNaN(Number(to))) { setVal(null); return; }
    const end=Number(to);
    const startTime=performance.now();
    function tick(now) {
      const p=Math.min((now-startTime)/duration,1);
      const e=1-Math.pow(1-p,3);
      setVal(e*end);
      if(p<1) ref.current=requestAnimationFrame(tick);
    }
    ref.current=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(ref.current);
  }, [to, duration]);
  if(val===null) return <span>—</span>;
  return <span>{prefix}{val.toFixed(dec)}{suffix}</span>;
}

function WellSelector() {
  const { wells, selectedWell, setSelectedWell, wellsLoading } = useWell();
  if(wellsLoading) return null;
  const shortName=w=>w.split("/").pop();
  const sorted=[...wells].sort((a,b)=>{
    const na=shortName(a), nb=shortName(b);
    const aFirst=na==="WELL_1"||na.endsWith("_1")?0:1;
    const bFirst=nb==="WELL_1"||nb.endsWith("_1")?0:1;
    if(aFirst!==bFirst) return aFirst-bFirst;
    return na.localeCompare(nb);
  });
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <span style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1, textTransform:"uppercase" }}>Well</span>
      <select value={selectedWell} onChange={e=>setSelectedWell(e.target.value)} style={{
        background:C.panel, border:`1px solid ${C.border2}`, borderRadius:6,
        color:C.cyan, fontFamily:MONO, fontSize:10, padding:"5px 10px",
        outline:"none", cursor:"pointer", maxWidth:180,
      }}>
        {sorted.map(w=>(<option key={w} value={w}>{shortName(w)}</option>))}
      </select>
    </div>
  );
}

/* PULSE DOT */
function PulseDot({ color=C.green, size=10 }) {
  return (
    <span style={{ position:"relative", display:"inline-block", width:size, height:size, flexShrink:0 }}>
      <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:color, animation:"pulse-ring 1.8s ease-out infinite", opacity:0 }}/>
      <span style={{ position:"absolute", inset:2, borderRadius:"50%", background:color, boxShadow:`0 0 6px ${color}` }}/>
    </span>
  );
}

/* TICKER BAR*/
function TickerBar({ data }) {
  if(!data) return null;
  const items=Object.entries(data).slice(0,14).map(([k,v])=>
    `${k.replace(/([A-Z])/g," $1").trim()}: ${v?.mean?.toFixed(2)??"—"}`
  ).join("   •   ");
  return (
    <div className="ticker-bar" title="Hover to pause" style={{ background:C.panel, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, height:32, overflow:"hidden", whiteSpace:"nowrap", position:"relative" }}>
      <span className="ticker-text" style={{ display:"inline-block", color:C.muted, fontFamily:MONO, fontSize:12, lineHeight:"32px", animation:"ticker-scroll 300s linear infinite", paddingLeft:"100vw" }}>
        {items}   •   {items}
      </span>
      <div style={{ position:"absolute", left:0, top:0, bottom:0, width:80, background:`linear-gradient(to right, ${C.panel}, transparent)` }}/>
      <div style={{ position:"absolute", right:0, top:0, bottom:0, width:80, background:`linear-gradient(to left, ${C.panel}, transparent)` }}/>
    </div>
  );
}

/* STAT CARD*/
function StatCard({ label, value, unit, color, sub, icon, delay=0, context }) {
  return (
    <div className="card-hover" style={{
      background:C.card, border:`1px solid ${C.border}`, borderTop:`3px solid ${color}`,
      borderRadius:12, padding:"18px 20px",
      animation:`fade-up 0.5s ease ${delay}s both`, position:"relative", overflow:"hidden"
    }}>
      <div style={{ position:"absolute", top:-30, right:-30, width:90, height:90, borderRadius:"50%", background:`${color}10`, pointerEvents:"none" }}/>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:6 }}>
        <div style={{ fontSize:18 }}>{icon}</div>
        {context && (
          <div style={{ fontFamily:MONO, fontSize:8, color, background:`${color}14`, border:`1px solid ${color}30`, borderRadius:4, padding:"2px 6px", letterSpacing:1, textTransform:"uppercase", whiteSpace:"nowrap" }}>{context}</div>
        )}
      </div>
      <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:24, fontWeight:700, color, lineHeight:1 }}>
        <AnimNum to={value} dec={1} suffix={unit}/>
      </div>
      {sub && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

/* CHART CARD*/
function ChartCard({ title, subtitle, children, height=260, delay=0, accentColor=C.cyan }) {
  return (
    <div style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"20px 16px 20px",
      animation:`fade-up 0.5s ease ${delay}s both`,
      position:"relative", overflow:"visible"
    }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${accentColor}88, transparent)` }}/>
      <div style={{ position:"absolute", left:0, right:0, height:1, background:`linear-gradient(to right, transparent, ${accentColor}22, transparent)`, animation:"scan-line 8s linear infinite", pointerEvents:"none" }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      <div style={{ height }}>{children}</div>
    </div>
  );
}

/* FIELD METADATA*/
const FIELD_META = {
  CsgAtOpen:              { unit:"psi",    label:"Casing Pressure @ Open",    advice:v=>v<300?"▲ Low — may not have enough gas to lift plunger. Increase shut-in time.":v>800?"◆ High — good reservoir pressure. Consider opening valve sooner.":"◆ Normal range. Monitor trends." },
  TbgAtOpen:              { unit:"psi",    label:"Tubing Pressure @ Open",    advice:v=>v>200?"▲ High tubing pressure — liquid loading possible. Check after-flow.":"◆ Normal. Low tubing pressure is good for gas production." },
  LineAtOpen:             { unit:"psi",    label:"Line Pressure @ Open",      advice:()=>"Line pressure = downstream pipeline. High line pressure reduces production rate." },
  CsgAtClose:             { unit:"psi",    label:"Casing Pressure @ Close",   advice:v=>v<200?"▲ Very low at close — well may be depleting gas too fast.":"◆ Casing drained properly during cycle." },
  TbgAtClose:             { unit:"psi",    label:"Tubing Pressure @ Close",   advice:()=>"Tubing at close shows backpressure when valve shuts." },
  LineAtClose:            { unit:"psi",    label:"Line Pressure @ Close",     advice:()=>"Pipeline back-pressure at valve close event." },
  CsgCycleMin:            { unit:"psi",    label:"Min Casing Pressure",       advice:v=>v<150?"▲ Very low minimum — reservoir may need more shut-in time to recover.":"◆ OK minimum pressure." },
  ArrivalVelocity:        { unit:"ft/min", label:"Plunger Arrival Velocity",  advice:v=>v<350?"▽ Too slow (<350) — plunger may drop back. Increase shut-in time or reduce line pressure.":v>1200?"▶ Too fast (>1200) — excess energy, reduce shut-in time to save gas.":"◆ Optimal range (350–1200 ft/min). Perfect lift!" },
  ArrivalMinute:          { unit:"min",    label:"Plunger Arrival Time",      advice:v=>v>60?"▲ Late arrival — plunger taking too long. Check for liquid loading or low casing pressure.":v<5?"▲ Very fast arrival — may indicate no liquid slug.":"◆ Normal arrival time." },
  VentMin:                { unit:"min",    label:"Vent Duration",             advice:v=>v>30?"▲ Long vent — high backpressure or line issues. Check separator.":"◆ Vent time is normal." },
  TimeAfterFlow:          { unit:"min",    label:"After-Flow Duration",       advice:v=>v<10?"▲ Short after-flow — closing valve too early. Extend to capture more gas.":v>120?"▲ Very long after-flow — may delay next cycle. Balance production vs cycle rate.":"◆ Good after-flow window." },
  TimeOffTotal:           { unit:"min",    label:"Total Shut-In Time",        advice:v=>v<30?"▲ Short shut-in — casing may not build enough pressure. Increase to 45–90 min.":v>300?"▲ Very long shut-in — wasting production time. Reduce if casing pressure is sufficient.":"◆ Shut-in time is in good range." },
  FlowRateAtClose:        { unit:"MCF/d",  label:"Flow Rate @ Close",         advice:v=>v<50?"▲ Low flow rate — well may be under-performing. Check valve, plunger, separator.":v>500?"◆ Excellent production rate!":"◆ Normal flow rate." },
  VolCycleTotal:          { unit:"MCF",    label:"Total Cycle Volume",        advice:v=>v<0.5?"▲ Low cycle volume — increase after-flow time or review plunger sizing.":"◆ Good gas volume per cycle." },
  ShutInEfficiency:       { unit:"×",      label:"Shut-In Efficiency",        advice:v=>v<0.8?"▲ Over-shut-in — well sat too long. Reduce shut-in time.":v>1.2?"▲ Under-shut-in — opened valve too soon. Increase shut-in time.":"◆ Optimal shut-in efficiency (close to 1.0)." },
  BuildupRate_psi_per_min:{ unit:"psi/min",label:"Pressure Buildup Rate",    advice:v=>v<0.5?"▲ Slow buildup — reservoir permeability may be low. Wait longer before opening.":v>5?"◆ Fast buildup — healthy reservoir. Shorter shut-in may work.":"◆ Normal buildup rate." },
  LiquidHeight_ft:        { unit:"ft",     label:"Liquid Column Height",      advice:v=>v>500?"▲ High liquid load — well is loading up. Extend shut-in or reduce after-flow.":v<50?"◆ Minimal liquid — well is running dry/gaseous.":"◆ Manageable liquid height." },
  afterflow_pct:          { unit:"%",      label:"After-Flow % of Cycle",     advice:v=>v<20?"▲ Very little after-flow — extend production window.":v>80?"▲ Mostly after-flow — increase shut-in time for better recovery.":"◆ Balanced cycle composition." },
  shutin_pct:             { unit:"%",      label:"Shut-In % of Cycle",        advice:v=>v<20?"▲ Very short shut-in — casing may not build adequate pressure.":v>80?"▲ Mostly shut-in — extend after-flow to capture more gas.":"◆ Good shut-in/production balance." },
  gas_travel_min:         { unit:"min",    label:"Gas Travel Time (Eq.14)",   advice:v=>v>20?"▲ Long gas travel — liquid column may be deep or tubing is partially obstructed.":"◆ Normal gas travel time." },
  liquid_travel_min:      { unit:"min",    label:"Liquid Travel Time (Eq.14)",advice:v=>v>30?"▲ Long liquid travel — large liquid slug or low casing pressure. Consider shorter cycles.":"◆ Normal liquid travel time." },
  Casing:                 { unit:"psi",    label:"Casing Pressure (Distribution)", advice:v=>v<300?"▲ Low casing pressure bin — many cycles starting under-pressured.":v>800?"◆ High pressure bin — reservoir is charged.":"◆ Normal casing pressure range." },
  Tubing:                 { unit:"psi",    label:"Tubing Pressure (Distribution)", advice:v=>v>200?"▲ High tubing pressure bin — check for liquid loading.":"◆ Normal tubing pressure distribution." },
  Line:                   { unit:"psi",    label:"Line Pressure (Distribution)",   advice:()=>"Line pressure distribution — high bins suggest persistent pipeline back-pressure." },
  count:                  { unit:"cycles", label:"Cycle Count",                    advice:v=>v>50?"◆ High frequency bin — this operating range is most common.":v<5?"▲ Rare operating range — outlier conditions detected.":"◆ Normal distribution bin." },
  "After-Flow %":         { unit:"%",      label:"After-Flow %",                   advice:v=>v<20?"▲ Low after-flow — extend production window.":v>80?"▲ Mostly after-flow — increase shut-in time.":"◆ Balanced cycle." },
  "Shut-In %":            { unit:"%",      label:"Shut-In %",                      advice:v=>v>80?"▲ Mostly shut-in — extend after-flow to capture more gas.":"◆ Good shut-in/production balance." },
  q_close:                { unit:"MCF/d",  label:"Closing Rate",                   advice:()=>"Flow rate when the valve closed. Compare against the break-even (average cycle) rate." },
  q_recycle:              { unit:"MCF/d",  label:"Break-Even Rate",                advice:()=>"Average cycle rate (shut-in included). Closing below this rate favours recycling." },
  remaining_pct:          { unit:"%",      label:"Initial Rate Remaining",         advice:v=>v<40?"▲ Depleted — less than 40% of the initial rate was left at close.":"◆ Healthy share of the initial rate still flowing at close." },
  effective_min:          { unit:"min",    label:"Useful Buildup",                 advice:()=>"Shut-in minutes the casing genuinely needed at this well's normal buildup rate." },
  plateau_min:            { unit:"min",    label:"Plateau Time",                   advice:v=>v>=3?"▲ Plateau — the casing added little pressure for this long.":"◆ Negligible plateau." },
  value:                  { unit:"",       label:"Scalar Value",                   advice:v=>v!=null?`Logged scalar = ${v.toFixed(4)}. Compare against your baseline run.`:"Scalar logged from TensorBoard." },
};

/* ── renderAdvice ── */
function renderAdvice(text) {
  if(!text) return null;
  const ICONS = {
    "▲": (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round"/><line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round"/><circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b"/></svg>),
    "■": (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422"/><path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/></svg>),
    "◆": (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22"/><path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>),
    "▽": (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>),
    "▶": (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}><path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  };
  for(const [sym,icon] of Object.entries(ICONS)) {
    if(text.startsWith(sym+" ")) return (<span style={{display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.5}}>{icon}<span>{text.slice(sym.length+1)}</span></span>);
  }
  return <span>{text}</span>;
}

/* TOOLTIP PORTAL*/
function TooltipPortal({ children, x, y }) {
  return createPortal(
    <div style={{ position:"fixed", left:x, top:y, zIndex:999999, pointerEvents:"none" }}>{children}</div>,
    document.body
  );
}

/* CUSTOM TOOLTIP*/
function TTip({ active, payload, label }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);
  const [pos, setPos] = useState({ x:0, y:0 });

  const raw      = active&&payload?.length ? payload[0]?.payload||{} : {};
  const cycleNum = raw.CycleSerialNumber??raw.cycle??raw.index??null;
  const timeStr  = raw.time||raw.timestamp||raw.date||null;
  const fieldKey = raw._actualFieldKey||null;

  let primaryField=null, primaryValue=null, primaryUnit="";
  if(active&&payload?.length) {
    for(const p of payload) {
      const rawKey=p.dataKey||p.name||"";
      const genericKeys=new Set(["count","value","bin","Casing","Tubing","Line","actual","lstm","target","step","ShutInEfficiency"]);
      const lookupKey=(genericKeys.has(rawKey)||rawKey==="actual"||rawKey==="lstm")&&fieldKey?fieldKey:rawKey;
      const meta=FIELD_META[lookupKey]||FIELD_META[rawKey];
      if(meta&&typeof p.value==="number"&&primaryField===null) { primaryField=lookupKey; primaryValue=p.value; primaryUnit=meta.unit||""; }
    }
  }

  const context={};
  Object.entries(raw).forEach(([k,v])=>{ if(typeof v==="number"&&!k.startsWith("_")&&k!=="index") context[k]=Math.round(v*100)/100; });

  useEffect(() => {
    if(!active||!primaryField||primaryValue==null) return;
    const rounded=typeof primaryValue==="number"?Math.round(primaryValue*10)/10:primaryValue;
    const key=`${primaryField}_${rounded}`;
    if(prevKeyRef.current===key) return;
    const timer=setTimeout(()=>{
      prevKeyRef.current=key;
      const allFieldsWithStatus=Object.entries(raw)
        .filter(([k,v])=>typeof v==="number"&&!k.startsWith("_")&&k!=="index")
        .map(([k,v])=>{ const meta=FIELD_META[k]; const entry={ field:k, label:meta?.label||k, value:Math.round(v*100)/100, unit:meta?.unit||"" }; if(raw._actualStatus) entry.status=raw._actualStatus; return entry; });
      fetchAdvice(primaryField,primaryValue,primaryUnit,cycleNum,context,allFieldsWithStatus.length>0?allFieldsWithStatus:[]);
    },400);
    return ()=>clearTimeout(timer);
  }, [active, primaryField, primaryValue]);

  useEffect(() => { if(!active) clearAdvice(); }, [active]);

  useEffect(() => {
    function onMove(e) {
      const TW=330,TH=430;
      const x=e.clientX+18, y=e.clientY-10;
      setPos({ x:x+TW>window.innerWidth?e.clientX-TW-18:x, y:y+TH>window.innerHeight?window.innerHeight-TH-16:y });
    }
    if(active) window.addEventListener("mousemove",onMove);
    return ()=>window.removeEventListener("mousemove",onMove);
  }, [active]);

  if(!active||!payload?.length) return null;

  return (
    <TooltipPortal x={pos.x} y={pos.y}>
      <div style={{ background:C.card, border:`1px solid ${C.border2}`, borderRadius:12, padding:"12px 16px", fontFamily:MONO, fontSize:11, boxShadow:"0 12px 40px rgba(0,0,0,0.8)", maxWidth:320, maxHeight:420, overflowY:"auto" }}>
        <div style={{ borderBottom:`1px solid ${C.border}`, paddingBottom:7, marginBottom:8 }}>
          {cycleNum!=null && <div style={{ color:C.cyan, fontWeight:700, fontSize:12, marginBottom:2 }}>Cycle {cycleNum}</div>}
          {timeStr && <div style={{ color:C.muted, fontSize:9 }}>{timeStr}</div>}
        </div>
        {payload.map((p,i)=>{
          const rawKey=p.dataKey||p.name||"";
          const genericKeys=new Set(["count","value","bin","actual","lstm","target","step"]);
          const isGeneric=genericKeys.has(rawKey);
          const lookupKey=isGeneric&&fieldKey?fieldKey:rawKey;
          const meta=FIELD_META[lookupKey]||FIELD_META[rawKey];
          let displayName;
          if(rawKey==="actual"&&meta?.label)      displayName=`Actual — ${meta.label}`;
          else if(rawKey==="lstm"&&meta?.label)   displayName=`Deep Sequence Neural Network — ${meta.label}`;
          else if(rawKey==="target")              displayName="Target";
          else if(rawKey==="count"&&meta?.label)  displayName=`${meta.label}`;
          else if(rawKey==="value"&&meta?.label)  displayName=meta.label;
          else if(rawKey==="Casing")              displayName="Casing Pressure";
          else if(rawKey==="Tubing")              displayName="Tubing Pressure";
          else if(rawKey==="Line")                displayName="Line Pressure";
          else if(rawKey==="ShutInEfficiency")    displayName="Shut-In Efficiency";
          else                                    displayName=meta?.label||p.name||rawKey;
          const isCountBar=rawKey==="count"||rawKey==="Casing"||rawKey==="Tubing"||rawKey==="Line";
          const unit=isCountBar?"cycles":(meta?.unit||"");
          const formatted=typeof p.value==="number"?(Math.abs(p.value)>=100?p.value.toFixed(1):p.value.toFixed(3)):String(p.value??"—");
          return (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:4, paddingBottom:4, borderBottom:i<payload.length-1?`1px solid ${C.border}`:"none" }}>
              <div style={{ color:C.muted, fontSize:10, flex:1, lineHeight:1.3 }}>{displayName}</div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <span style={{ color:p.color||C.cyan, fontWeight:700, fontSize:12 }}>{formatted}</span>
                {unit && <span style={{ color:C.muted, fontSize:9, marginLeft:3 }}>{unit}</span>}
              </div>
            </div>
          );
        })}
        <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${C.border}`, fontSize:10, lineHeight:1.6, background:loading?`${C.muted}08`:`${C.cyan}08`, borderRadius:6, padding:"6px 8px" }}>
          {loading ? <span style={{ color:C.muted }}>Getting AI recommendation...</span>
                   : advice ? renderAdvice(advice)
                   : <span style={{ color:C.muted }}>Hover a data point for AI advice</span>}
        </div>
      </div>
    </TooltipPortal>
  );
}

function cycleTickFmt(v) {
  if(v==null||v==="") return "";
  const n=Number(v); if(!isNaN(n)) return `C${n}`;
  if(String(v).includes(":")||String(v).includes("-")) return String(v).slice(0,8);
  return String(v);
}
function CYCLE_XAXIS_PROPS() {
  return { tick:{ fill:C.muted, fontSize:9, fontFamily:MONO }, tickLine:false, tickFormatter:cycleTickFmt };
}

const CHART_MARGIN_DEFAULT = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_DUAL_Y  = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_SCATTER = { top:8, right:14, left:-10, bottom:36 };
const CHART_MARGIN_HISTO   = { top:8, right:14, left:-10, bottom:36 };

const LEGEND_PROPS = {
  wrapperStyle:{ fontFamily:MONO, fontSize:10, paddingBottom:4 },
  verticalAlign:"top", align:"right", iconSize:8,
};

function LoadingScreen() {
  const [dots,setDots]=useState(0);
  useEffect(()=>{ const t=setInterval(()=>setDots(d=>(d+1)%4),400); return ()=>clearInterval(t); },[]);
  return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:32 }}>
      <div style={{ position:"relative", width:100, height:100 }}>
        <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:`2px solid ${C.cyan}44`, borderTopColor:C.cyan, animation:"spin-slow 1.2s linear infinite" }}/>
        <div style={{ position:"absolute", inset:14, borderRadius:"50%", border:`2px solid ${C.teal}33`, borderBottomColor:C.teal, animation:"counter-spin 1.8s linear infinite" }}/>
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="5" y2="10"/><line x1="12" y1="2" x2="19" y2="10"/><line x1="5" y1="10" x2="19" y2="10"/><line x1="7" y1="10" x2="5" y2="22"/><line x1="17" y1="10" x2="19" y2="22"/><line x1="5" y1="22" x2="19" y2="22"/><line x1="12" y1="2" x2="12" y2="22"/><line x1="8" y1="14" x2="16" y2="14"/><line x1="7" y1="18" x2="17" y2="18"/><rect x="10" y="19" width="4" height="3" rx="0.5"/></svg>
        </div>
      </div>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontWeight:800, fontSize:22, color:C.cyan, letterSpacing:3, textTransform:"uppercase" }}>PLUNGER LIFT SYSTEM</div>
        <div style={{ fontFamily:MONO, fontSize:12, color:C.muted, marginTop:8 }}>Connecting to backend{"...".slice(0,dots)}</div>
      </div>
      <div style={{ width:240, height:3, background:C.border, borderRadius:2, overflow:"hidden" }}>
        <div style={{ height:"100%", borderRadius:2, background:`linear-gradient(to right, ${C.cyan}, ${C.teal})`, backgroundSize:"200% 100%", animation:"shimmer 1.4s ease infinite" }}/>
      </div>
    </div>
  );
}

function ErrorScreen({ msg, retry }) {
  return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:24, padding:40 }}>
      <div style={{ display:"flex", justifyContent:"center" }}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ff3d5a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div>
      <div style={{ fontFamily:SANS, fontWeight:700, fontSize:20, color:C.red, textAlign:"center" }}>Backend Not Connected</div>
      <div style={{ fontFamily:MONO, fontSize:12, color:C.muted, textAlign:"center", maxWidth:480, lineHeight:1.8 }}>{msg}</div>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:"16px 24px", fontFamily:MONO, fontSize:12 }}>
        <div style={{ color:C.muted, marginBottom:8 }}>Run in your backend folder:</div>
        <div style={{ color:C.teal }}>$ python app.py</div>
      </div>
      <button onClick={retry} style={{ background:`linear-gradient(135deg, ${C.cyan}, ${C.teal})`, color:"#000", border:"none", borderRadius:8, padding:"12px 28px", fontFamily:SANS, fontWeight:700, fontSize:14, cursor:"pointer" }}>Retry Connection</button>
    </div>
  );
}

/* MAIN DASHBOARD*/
function Dashboard({ user, onLogout }) {
  injectCSS(GLOBAL_CSS);
  const { isDark, toggle } = useTheme();
  const D = useMemo(()=>getColors(isDark),[isDark]);

  useEffect(()=>{ applyTheme(D); document.body.style.background=D.bg; document.body.style.color=D.text; },[D]);

  const { apiUrl, selectedWell } = useWell();

  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [summary, setSummary]       = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [arrStats, setArrStats]     = useState(null);
  const [pressDist, setPressDist]   = useState(null);
  const [scatterData, setScatterData] = useState(null);
  const [wellSpecs, setWellSpecs]   = useState(null);
  const [modelInfo, setModelInfo]   = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [predLoading, setPredLoading] = useState(false);

  const [liveIdx, setLiveIdx] = useState(0);
  const tickRef = useRef(0);
  const [tick, setTick] = useState(0);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s,ts,arr,pd,sc,ws,mi] = await Promise.all([
        axios.get(apiUrl("/cycles/summary")),
        axios.get(apiUrl("/cycles/timeseries?limit=200")),
        axios.get(apiUrl("/cycles/arrival-stats")),
        axios.get(apiUrl("/cycles/pressure-distribution")),
        axios.get(apiUrl("/cycles/scatter")),
        axios.get(`${API}/well-specs`),
        axios.get(`${API}/model-info`),
      ]);
      setSummary(s.data); setTimeseries(ts.data); setArrStats(arr.data);
      setPressDist(pd.data); setScatterData(sc.data); setWellSpecs(ws.data); setModelInfo(mi.data);
    } catch(e) { setError(e.message||"Cannot connect to backend"); }
    setLoading(false);
  }, [apiUrl]);

  useEffect(()=>{ const t=setTimeout(()=>fetchAll(),80); return ()=>clearTimeout(t); },[fetchAll]);

  useEffect(()=>{
    if(!timeseries?.data?.length) return;
    const t=setInterval(()=>{
      setLiveIdx(i=>(i+1)%timeseries.data.length);
      tickRef.current+=1; setTick(tickRef.current);
    },1200);
    return ()=>clearInterval(t);
  },[timeseries]);

  const runPredict = async () => {
    setPredLoading(true);
    try {
      const res=await axios.post(`${API}/predict/batch`,{ n:15 });
      const data=res.data;
      setPredictions({ predictions:data.wells||[], total_wells:data.total_wells||0, forecast_cycles:data.forecast_cycles||15 });
    } catch(e) {
      console.error("Prediction failed:",e);
      alert("Prediction failed: "+(e.response?.data?.error||e.message));
      setPredictions({ predictions:[] });
    }
    setPredLoading(false);
  };

  const tsData = useMemo(()=>{
    const raw=timeseries?.data||[];
    return raw.map(pt=>{
      const vel=pt.ArrivalVelocity;
      let velStatus=undefined;
      if(typeof vel==="number") {
        if(vel>=350&&vel<=1200)      velStatus="GOOD — velocity within optimal range (350–1200 ft/min)";
        else if(vel>1200&&vel<=1500) velStatus="WARNING — velocity slightly high (1200–1500 ft/min), monitor energy use";
        else if(vel>1500)            velStatus="CRITICAL — velocity too fast (>1500 ft/min), risk of equipment damage";
        else if(vel>=200&&vel<350)   velStatus="WARNING — velocity low (200–350 ft/min), plunger struggling";
        else if(vel<200)             velStatus="CRITICAL — velocity too slow (<200 ft/min), plunger likely not arriving";
      }
      return velStatus?{ ...pt, _actualStatus:velStatus }:pt;
    });
  },[timeseries]);

  const tsCols = useMemo(()=>timeseries?.columns||[],[timeseries]);
  const currentPoint = tsData[liveIdx]||{};

  const COL_COLORS = useMemo(()=>({
    CsgAtOpen:C.cyan, TbgAtOpen:C.teal, LineAtOpen:C.orange,
    ArrivalVelocity:C.amber, ArrivalMinute:C.pink, VentMin:C.purple,
    CsgAtClose:C.purple, TbgAtClose:C.lime, LineAtClose:C.red,
    CsgCycleMin:C.red, FlowRateAtClose:C.green, VolCycleTotal:C.amber,
    TimeAfterFlow:C.teal, TimeOffTotal:C.pink,
  }),[isDark]);

  const histData = useMemo(()=>arrStats
    ? arrStats.bins.map((b,i)=>({ bin:b.toFixed(0), count:arrStats.counts[i], _actualFieldKey:"ArrivalVelocity" }))
    : []
  ,[arrStats]);

  const pressData = useMemo(()=>pressDist?.CsgAtOpen?.bins?.map((b,i)=>({
    bin:b.toFixed(0),
    Casing:pressDist.CsgAtOpen?.counts[i]||0,
    Tubing:pressDist.TbgAtOpen?.counts[i]||0,
    Line:pressDist.LineAtOpen?.counts[i]||0,
    _actualFieldKey:"CsgAtOpen",
  }))||[]
  ,[pressDist]);

  if(loading) return <LoadingScreen/>;
  if(error)   return <ErrorScreen msg={error} retry={fetchAll}/>;

  const TABS = [
    { id:"overview",          label:"Overview",          icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
    { id:"aicontroller",      label:"AI Controller",     icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg> },
    { id:"glr",               label:"GLR Check",         icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="15" y2="22"/><line x1="4" y1="9" x2="14" y2="9"/><path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18"/><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5"/></svg> },
    { id:"insights",          label:"AI Insights",       icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg> },
    { id:"anomaly",           label:"Anomaly Detection", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,17 6,11 9,14 12,8 15,13 18,7 22,12"/><line x1="20" y1="9" x2="20" y2="21"/><line x1="17" y1="21" x2="23" y2="21"/></svg> },
    { id:"rootcause",         label:"Root Cause",        icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><circle cx="11" cy="11" r="3"/></svg> },
    { id:"pressurevelocity",      label:"Pressure & Velocity",     icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
    { id:"shutin",            label:"Shut-In",           icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="3"/><line x1="9" y1="9" x2="9" y2="15"/><line x1="15" y1="9" x2="15" y2="15"/></svg> },
    { id:"afterflow",         label:"After-Flow",        icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/></svg> },
    { id:"model",             label:"Model",             icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/></svg> },
    { id:"ComparativeAnalysis",label:"Benchmarking",    icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> },
    { id:"epa", label:"EPA Compliance", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
    { id:"gapl", label:"GAPL", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/></svg> },
    { id:"pagl", label:"PAGL", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/></svg> },
    { id:"gaslift", label:"Gas Lift", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20"/></svg> },
    { id:"mandrel", label:"Mandrel Valves", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="2" width="12" height="20" rx="2"/></svg> },
    { id:"injection", label:"Injection", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v6l4 4-4 4v6"/></svg> },
    { id:"methodcomparison", label:"Method Comparison", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18"/><rect x="14" y="10" width="7" height="11"/></svg> },
    // { id:"tensorboard",       label:"TensorBoard",      icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="3"/><circle cx="5" cy="19" r="3"/><circle cx="19" cy="19" r="3"/><path d="M12 8v3"/><line x1="12" y1="11" x2="5" y2="16"/><line x1="12" y1="11" x2="19" y2="16"/></svg> },
  ];

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex" }}>
      {/* SIDEBAR */}
      <aside style={{
        width:sidebarOpen?220:0, minHeight:"100vh", background:C.panel,
        borderRight:sidebarOpen?`1px solid ${C.border}`:"none",
        display:"flex", flexDirection:"column",
        position:"sticky", top:0, height:"100vh",
        zIndex:200, flexShrink:0, overflow:"hidden",
        transition:"width 0.25s ease",
      }}>
        <div style={{
          padding:"20px 16px 18px", borderBottom:`1px solid ${C.border}`,
          background:`linear-gradient(160deg, ${C.cyan}0a 0%, transparent 60%)`,
          position:"relative", overflow:"hidden", width:220, whiteSpace:"nowrap",
        }}>
          <button onClick={()=>setSidebarOpen(o=>!o)} title="Collapse sidebar" style={{
            position:"absolute", top:10, right:10, width:24, height:24, borderRadius:5,
            background:"transparent", border:`1px solid ${C.border2}`,
            color:C.muted, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.18s",
          }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=C.cyan;e.currentTarget.style.color=C.cyan;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border2;e.currentTarget.style.color=C.muted;}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
            <div style={{ position:"relative", width:36, height:36, flexShrink:0 }}>
              <svg style={{ position:"absolute", inset:0, animation:"spin-slow 6s linear infinite" }} width="36" height="36" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="16" fill="none" stroke={C.cyan} strokeWidth="1" strokeDasharray="8 4" strokeLinecap="round" opacity="0.5"/>
              </svg>
              <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.cyan} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{filter:`drop-shadow(0 0 4px ${C.cyan}88)`}}>
                  <line x1="12" y1="2" x2="5" y2="10"/><line x1="12" y1="2" x2="19" y2="10"/>
                  <line x1="5" y1="10" x2="19" y2="10"/><line x1="7" y1="10" x2="5" y2="22"/>
                  <line x1="17" y1="10" x2="19" y2="22"/><line x1="5" y1="22" x2="19" y2="22"/>
                  <line x1="12" y1="2" x2="12" y2="22"/><line x1="8" y1="14" x2="16" y2="14"/>
                  <line x1="7" y1="18" x2="17" y2="18"/><rect x="10" y="19" width="4" height="3" rx="0.5"/>
                </svg>
              </div>
            </div>
            <div>
              <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontWeight:800, fontSize:15, letterSpacing:2, textTransform:"uppercase", lineHeight:1.15, background:`linear-gradient(90deg, ${C.cyan} 0%, ${C.teal} 100%)`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>Plunger Lift</div>
              <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontWeight:800, fontSize:15, letterSpacing:2, textTransform:"uppercase", lineHeight:1.15, background:`linear-gradient(90deg, ${C.teal} 0%, ${C.cyan} 100%)`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>System</div>
            </div>
          </div>
          <div style={{ height:1, background:`linear-gradient(90deg, ${C.cyan}66, ${C.teal}44, transparent)`, marginBottom:8 }}/>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:5, height:5, borderRadius:"50%", background:C.green, boxShadow:`0 0 6px ${C.green}` }}/>
            <div style={{ fontFamily:MONO, fontSize:8, color:C.teal, letterSpacing:2, textTransform:"uppercase" }}>AI DASHBOARD</div>
          </div>
        </div>

        <nav style={{ flex:1, padding:"12px 12px", display:"flex", flexDirection:"column", gap:4, overflowY:"auto", width:220 }}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              background:tab===t.id?`${C.cyan}15`:"transparent",
              color:tab===t.id?C.cyan:C.muted,
              border:`1px solid ${tab===t.id?C.cyan+"44":"transparent"}`,
              borderRadius:8, padding:"10px 14px", fontFamily:SANS, fontWeight:700,
              fontSize:11, letterSpacing:1, textTransform:"uppercase",
              cursor:"pointer", textAlign:"left",
              display:"flex", alignItems:"center", gap:10,
              transition:"all 0.18s ease", width:"100%",
            }}
            onMouseEnter={e=>{ if(tab!==t.id){e.currentTarget.style.background=`${C.cyan}08`;e.currentTarget.style.color=C.text;} }}
            onMouseLeave={e=>{ if(tab!==t.id){e.currentTarget.style.background="transparent";e.currentTarget.style.color=C.muted;} }}>
              <span style={{ fontSize:14 }}>{t.icon}</span>
              <span>{t.label}</span>
              {tab===t.id && <span style={{ marginLeft:"auto", width:4, height:4, borderRadius:"50%", background:C.cyan, boxShadow:`0 0 6px ${C.cyan}` }}/>}
            </button>
          ))}
        </nav>

        <div style={{ padding:"14px 16px", borderTop:`1px solid ${C.border}`, display:"flex", flexDirection:"column", gap:10, width:220 }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", gap:4 }}>
            <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:1, textTransform:"uppercase" }}>Signed in as</div>
            <div style={{ fontFamily:MONO, fontSize:10, color:C.cyan, wordBreak:"break-all", lineHeight:1.4 }}>{user?.email}</div>
          </div>
          <button onClick={onLogout} style={{
            width:"100%", background:"transparent", border:`1.5px solid ${C.cyan}66`,
            borderRadius:7, color:C.cyan, cursor:"pointer",
            fontFamily:"'Barlow', sans-serif", fontSize:11, letterSpacing:1,
            padding:"9px 12px", textTransform:"uppercase", transition:"all 0.2s",
            display:"flex", alignItems:"center", gap:8, justifyContent:"center",
            fontWeight:700, boxShadow:`0 0 10px ${C.cyan}22`,
          }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=C.cyan;e.currentTarget.style.boxShadow=`0 0 18px ${C.cyan}44`;e.currentTarget.style.background=`${C.cyan}10`;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=`${C.cyan}66`;e.currentTarget.style.boxShadow=`0 0 10px ${C.cyan}22`;e.currentTarget.style.background="transparent";}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Logout
          </button>
        </div>
      </aside>

      {/* MAIN AREA */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
        <header style={{
          background:C.panel, borderBottom:`1px solid ${C.border}`,
          padding:"0 24px", height:52,
          display:"grid", gridTemplateColumns:"1fr auto 1fr", alignItems:"center", flexShrink:0,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {!sidebarOpen && (
              <button onClick={()=>setSidebarOpen(true)} title="Open sidebar" style={{
                width:30, height:30, borderRadius:6, background:"transparent", border:`1px solid ${C.border2}`,
                color:C.muted, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.18s",
              }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.cyan;e.currentTarget.style.color=C.cyan;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border2;e.currentTarget.style.color=C.muted;}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <PulseDot color={C.green} size={8}/>
              <span style={{ fontFamily:MONO, fontSize:11, color:C.green, letterSpacing:1 }}>LIVE</span>
            </div>
            <div style={{ width:1, height:16, background:C.border }}/>
            <WellSelector/>
            <div style={{ width:1, height:16, background:C.border }}/>
            <span style={{ fontFamily:MONO, fontSize:11, color:C.muted }}>CYC <span style={{ color:C.text, fontWeight:700 }}>C{currentPoint.CycleSerialNumber??liveIdx}</span></span>
            <div style={{ width:1, height:16, background:C.border }}/>
            <span style={{ fontFamily:MONO, fontSize:11, color:C.amber, fontWeight:700 }}>{currentPoint.ArrivalVelocity?.toFixed(0)??"—"} ft/min</span>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", gap:14 }}>
            <span style={{ fontFamily:MONO, fontSize:10, color:C.muted }}><span style={{ color:C.cyan }}>{user?.email}</span></span>
            <button onClick={toggle} title={isDark?"Switch to Light Mode":"Switch to Dark Mode"} style={{
              width:46, height:24, borderRadius:12,
              background:isDark?"#0f2040":"#d0daea",
              border:`1px solid ${isDark?C.cyan+"44":"#b8c8e0"}`,
              cursor:"pointer", padding:"0 3px", display:"flex", alignItems:"center",
              transition:"background 0.3s, border-color 0.3s", flexShrink:0,
            }}>
              <span style={{ width:18, height:18, borderRadius:"50%", background:isDark?C.cyan:"#006ea8", marginLeft:isDark?0:20, transition:"margin-left 0.3s, background 0.3s", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, lineHeight:1 }}>
                {isDark?"🌙":"☀️"}
              </span>
            </button>
            <button onClick={onLogout} style={{
              background:"transparent", border:`1.5px solid ${C.cyan}66`,
              borderRadius:6, color:C.cyan, cursor:"pointer",
              fontFamily:"'Barlow', sans-serif", fontSize:11, letterSpacing:1,
              padding:"6px 16px", textTransform:"uppercase", transition:"all 0.2s",
              display:"flex", alignItems:"center", gap:6, fontWeight:700, boxShadow:`0 0 10px ${C.cyan}22`,
            }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=C.cyan;e.currentTarget.style.boxShadow=`0 0 18px ${C.cyan}44`;e.currentTarget.style.background=`${C.cyan}10`;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=`${C.cyan}66`;e.currentTarget.style.boxShadow=`0 0 10px ${C.cyan}22`;e.currentTarget.style.background="transparent";}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Logout
            </button>
          </div>
        </header>

        <TickerBar data={summary}/>

        <main style={{ padding:"26px 26px 40px", flex:1 }}>
          {tab==="overview" && (
            <OverviewPage
              summary={summary} timeseries={timeseries} tsData={tsData} tsCols={tsCols}
              arrStats={arrStats} scatterData={scatterData} liveIdx={liveIdx}
              currentPoint={currentPoint} COL_COLORS={COL_COLORS} TTip={TTip}
            />
          )}
          {tab==="pressurevelocity" && (
            <PressureVelocityPage tsData={tsData} tsCols={tsCols} pressData={pressData} arrStats={arrStats} histData={histData} COL_COLORS={COL_COLORS} TTip={TTip}/>
          )}
          {tab==="shutin"   && <ShutInPage    TTip={TTip}/>}
          {tab==="afterflow"&& <AfterFlowPage TTip={TTip}/>}
          {tab==="model"    && (
            <ModelPage wellSpecs={wellSpecs} modelInfo={modelInfo} predictions={predictions} predLoading={predLoading} onRunPredict={runPredict}/>
          )}
          {tab==="tensorboard" && <TensorBoardPage TTip={TTip}/>}
          {tab==="insights"          && <InsightsPage/>}
          {tab==="rootcause"         && <RootCausePage/>}
          {tab==="anomaly"           && <AnomalyPage/>}
          {tab==="aicontroller"      && <AIControllerPage/>}
          {tab==="glr"               && <GLRFeasibilityPage/>}
          {tab==="ComparativeAnalysis"&& <ComparativeAnalysisPage wells={[]}/>}
          {tab==="epa" && <EPACompliancePage/>}
          {tab==="methodcomparison" && <MethodComparisonPage/>}
          {tab==="gapl" && <GAPLAnalysisPage/>}
          {tab==="pagl" && <PAGLAnalysisPage/>}
          {tab==="gaslift" && <GasLiftPerformancePage/>}
          {tab==="mandrel" && <MandrelValveManagerPage/>}
          {tab==="injection" && <InjectionDashboardPage/>}
        </main>

        <footer style={{ borderTop:`1px solid ${C.border}`, padding:"10px 26px", display:"flex", justifyContent:"space-between", alignItems:"center", background:C.panel }}>
          <div style={{ fontFamily:MONO, fontSize:10, color:C.muted }}>Plunger Lift AI · Well Performance & Optimization Platform</div>
          <div style={{ fontFamily:MONO, fontSize:10, color:C.muted2 }}>tick {tick}</div>
        </footer>
      </div>
    </div>
  );
}

/* AUTH WRAPPER */
function AppInner() {
  const { isDark } = useTheme();
  const [page, setPage] = useState(()=>{ try { return localStorage.getItem("plunger_user")?"dashboard":"signin"; } catch { return "signin"; } });
  const [user, setUser] = useState(()=>{ try { const s=localStorage.getItem("plunger_user"); return s?JSON.parse(s):null; } catch { return null; } });

  function handleSignIn(loggedInUser) { setUser(loggedInUser); setPage("dashboard"); }
  function handleSignOut() { localStorage.removeItem("plunger_user"); setUser(null); setPage("signin"); }

  if(page==="signin") return <SignIn onSignIn={handleSignIn} onGoSignUp={()=>setPage("signup")}/>;
  if(page==="signup") return <SignUp onGoSignIn={()=>setPage("signin")}/>;
  return <WellProvider><Dashboard user={user} onLogout={handleSignOut}/></WellProvider>;
}

export default function App() {
  return <ThemeProvider><AppInner/></ThemeProvider>;
}